/**
 * A venue's contract template (docs/sales.spec.md, "Choosing and signing"):
 * plain text the venue writes, filled in from the event and the couple's
 * choices, and turned into the HTML Pixel Sign signs. The venue brings its own
 * terms; this is only the mechanics.
 *
 * The text:
 * - a blank line starts a new paragraph
 * - `# Heading` and `## Subheading` on a line of their own
 * - lines starting `- ` make a list
 * - `**bold**` inside a line
 * - `{{field}}` from `CONTRACT_FIELDS`; the tables (`selections`, `totals`,
 *   `payment_schedule`) on a line of their own
 *
 * Signatures are added at the end by itself: one for each person signing, then
 * the venue's.
 */

import { dollarsFromCents } from './format.js'

export const CONTRACT_FIELDS = {
  couple_names: 'The names of the people signing',
  event_date: 'The event’s date, like Saturday, June 12, 2027',
  event_time: 'The event’s times, like 4:00 PM to 11:00 PM',
  guest_count: 'The number of guests',
  venue_name: 'The venue’s name',
  venue_address: 'The venue’s address',
  selections: 'A table of what the couple chose',
  totals: 'The subtotal, service charge, tax and total',
  payment_schedule: 'A table of the payments and when each is due',
  security_deposit: 'The security deposit and when it’s due, or nothing',
  valid_until: 'The last day the proposal can be signed',
} as const

export type ContractField = keyof typeof CONTRACT_FIELDS

const TABLE_FIELDS = ['selections', 'totals', 'payment_schedule'] as const
type TableField = (typeof TABLE_FIELDS)[number]
type InlineField = Exclude<ContractField, TableField>

function isTableField(name: ContractField): name is TableField {
  return TABLE_FIELDS.some((field) => field === name)
}

function isField(name: string): name is ContractField {
  return Object.hasOwn(CONTRACT_FIELDS, name)
}

const FIELD = /\{\{\s*([a-z_]+)\s*\}\}/g

/** Why a template can't be used, in plain words; empty when it can. */
export function templateErrors(body: string): string[] {
  const errors: string[] = []
  if (body.trim() === '') errors.push('A contract needs some text.')
  for (const match of body.matchAll(FIELD)) {
    const name = match[1] ?? ''
    if (!isField(name)) errors.push(`{{${name}}} isn’t a field you can use.`)
  }
  for (const paragraph of paragraphsOf(body)) {
    const alone = /^\{\{\s*([a-z_]+)\s*\}\}$/.exec(paragraph.trim())?.[1]
    for (const match of paragraph.matchAll(FIELD)) {
      const name = match[1] ?? ''
      if (isField(name) && isTableField(name) && alone !== name) errors.push(`{{${name}}} goes on a line of its own.`)
    }
  }
  return [...new Set(errors)]
}

export interface ContractLine {
  name: string
  description: string
  quantity: number
  unitCents: number
  totalCents: number
}

/** What a contract is filled in with, already in words where it's words. */
export interface ContractData {
  coupleNames: string
  eventDate: string
  eventTime: string
  guestCount: number
  venueName: string
  venueAddress: string
  selections: ContractLine[]
  totals: { subtotalCents: number; serviceChargeCents: number; taxCents: number; totalCents: number }
  schedule: { label: string; amountCents: number; due: string }[]
  securityDeposit: { amountCents: number; due: string } | null
  validUntil: string
  /** Everyone who signs, in order, the venue last. `key` names their signature fields. */
  signers: { key: string; name: string; role: string }[]
}

function escape(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
}

function paragraphsOf(body: string): string[] {
  return body
    .replace(/\r\n?/g, '\n')
    .split(/\n[ \t]*\n/)
    .map((p) => p.replace(/^\n+|\n+$/g, ''))
    .filter((p) => p.trim() !== '')
}

const money = (cents: number): string => dollarsFromCents(cents)

function inlineValue(name: InlineField, data: ContractData): string {
  switch (name) {
    case 'couple_names':
      return data.coupleNames
    case 'event_date':
      return data.eventDate
    case 'event_time':
      return data.eventTime
    case 'guest_count':
      return String(data.guestCount)
    case 'venue_name':
      return data.venueName
    case 'venue_address':
      return data.venueAddress
    case 'security_deposit':
      return data.securityDeposit === null ? '' : `${money(data.securityDeposit.amountCents)}, due ${data.securityDeposit.due}`
    case 'valid_until':
      return data.validUntil
    default: {
      const unexpected: never = name
      return unexpected
    }
  }
}

/**
 * A line of text: escaped, fields filled in, then `**bold**` made bold, so bold
 * can wrap a field. An asterisk in a filled-in value is escaped too, so a name
 * can't make anything bold.
 */
function inline(text: string, data: ContractData): string {
  return text
    .split(/(\{\{\s*[a-z_]+\s*\}\})/)
    .map((part) => {
      const name = /^\{\{\s*([a-z_]+)\s*\}\}$/.exec(part)?.[1]
      if (name !== undefined) {
        if (!isField(name) || isTableField(name)) return ''
        return escape(inlineValue(name, data)).replace(/\*/g, '&#42;')
      }
      return escape(part)
    })
    .join('')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
}

const TABLE = 'width:100%;border-collapse:collapse;margin:8px 0 16px'
const CELL = 'border:1px solid #ccc;padding:6px 8px;text-align:left;vertical-align:top'
const NUM = `${CELL};text-align:right;white-space:nowrap`

function table(field: TableField, data: ContractData): string {
  switch (field) {
    case 'selections': {
    const rows = data.selections
      .map(
        (l) =>
          `<tr><td style="${CELL}">${escape(l.name)}${l.description === '' ? '' : `<br><span style="color:#555">${escape(l.description)}</span>`}</td>` +
          `<td style="${NUM}">${String(l.quantity)}</td><td style="${NUM}">${money(l.unitCents)}</td><td style="${NUM}">${money(l.totalCents)}</td></tr>`,
      )
      .join('')
    return `<table style="${TABLE}"><thead><tr><th style="${CELL}">Item</th><th style="${NUM}">Qty</th><th style="${NUM}">Price</th><th style="${NUM}">Amount</th></tr></thead><tbody>${rows}</tbody></table>`
    }
    case 'totals': {
    const t = data.totals
    const row = (label: string, cents: number, bold = false) =>
      `<tr><td style="${CELL}">${bold ? `<strong>${label}</strong>` : label}</td><td style="${NUM}">${bold ? `<strong>${money(cents)}</strong>` : money(cents)}</td></tr>`
    return `<table style="${TABLE}"><tbody>${row('Subtotal', t.subtotalCents)}${row('Service charge', t.serviceChargeCents)}${row('Tax', t.taxCents)}${row('Total', t.totalCents, true)}</tbody></table>`
    }
    case 'payment_schedule': {
      const rows = data.schedule
        .map((p) => `<tr><td style="${CELL}">${escape(p.label)}</td><td style="${NUM}">${money(p.amountCents)}</td><td style="${CELL}">${escape(p.due)}</td></tr>`)
        .join('')
      return `<table style="${TABLE}"><thead><tr><th style="${CELL}">Payment</th><th style="${NUM}">Amount</th><th style="${CELL}">Due</th></tr></thead><tbody>${rows}</tbody></table>`
    }
    default: {
      const unexpected: never = field
      return unexpected
    }
  }
}

/** The signature block: a signature and the date for each signer, the venue last. */
function signatures(data: ContractData): string {
  const box = 'display:inline-block;width:260px;height:56px;border-bottom:1px solid #333'
  const date = 'display:inline-block;width:140px;height:24px;border-bottom:1px solid #333'
  const blocks = data.signers
    .map(
      (s) =>
        `<div style="margin:24px 0;break-inside:avoid"><p style="margin:0 0 4px"><strong>${escape(s.name)}</strong>, ${escape(s.role)}</p>` +
        `<span data-ps-field="sign.${escape(s.key)}" style="${box}"></span>&nbsp;&nbsp;` +
        `<span data-ps-field="date.${escape(s.key)}" style="${date}"></span></div>`,
    )
    .join('')
  return `<h2 style="font-size:14pt;margin:32px 0 8px">Signatures</h2>${blocks}`
}

/**
 * The contract as HTML: no scripts, links, forms or images, only inline styles,
 * and a `data-ps-field` box for each signer's signature and date, as Pixel Sign
 * takes it. Every word the venue or the couple typed is escaped.
 */
export function renderContract(body: string, data: ContractData): string {
  const blocks = paragraphsOf(body).map((paragraph) => {
    const trimmed = paragraph.trim()
    const alone = /^\{\{\s*([a-z_]+)\s*\}\}$/.exec(trimmed)?.[1]
    if (alone !== undefined && isField(alone) && isTableField(alone)) return table(alone, data)
    const lines = paragraph.split('\n')
    if (lines.length === 1 && trimmed.startsWith('## ')) return `<h2 style="font-size:14pt;margin:24px 0 8px">${inline(trimmed.slice(3), data)}</h2>`
    if (lines.length === 1 && trimmed.startsWith('# ')) return `<h1 style="font-size:18pt;margin:0 0 16px">${inline(trimmed.slice(2), data)}</h1>`
    if (lines.every((l) => l.trim().startsWith('- '))) {
      return `<ul style="margin:0 0 12px;padding-left:24px">${lines.map((l) => `<li>${inline(l.trim().slice(2), data)}</li>`).join('')}</ul>`
    }
    return `<p style="margin:0 0 12px">${lines.map((l) => inline(l, data)).join('<br>')}</p>`
  })
  return `<div style="font-family:Georgia,serif;font-size:12pt;line-height:1.5;color:#111">${blocks.join('')}${signatures(data)}</div>`
}

/** Made-up details for the template preview in the settings. */
export const SAMPLE_CONTRACT_DATA: ContractData = {
  coupleNames: 'Ada Okafor and Ben Ruiz',
  eventDate: 'Saturday, June 12, 2027',
  eventTime: '4:00 PM to 11:00 PM',
  guestCount: 120,
  venueName: 'Your venue',
  venueAddress: '100 Main Street',
  selections: [
    { name: 'Classic wedding', description: 'Ceremony and reception, six hours', quantity: 1, unitCents: 1_250_000, totalCents: 1_250_000 },
    { name: 'Plated dinner', description: '', quantity: 120, unitCents: 9_500, totalCents: 1_140_000 },
    { name: 'Extra hour', description: '', quantity: 1, unitCents: 75_000, totalCents: 75_000 },
  ],
  totals: { subtotalCents: 2_465_000, serviceChargeCents: 478_000, taxCents: 218_769, totalCents: 3_161_769 },
  schedule: [
    { label: 'Deposit', amountCents: 948_531, due: 'on signing' },
    { label: 'Second payment', amountCents: 1_000_000, due: 'December 12, 2026' },
    { label: 'Final payment', amountCents: 1_213_238, due: 'May 13, 2027' },
  ],
  securityDeposit: { amountCents: 100_000, due: 'May 13, 2027' },
  validUntil: 'November 4, 2026',
  signers: [
    { key: 's1', name: 'Ada Okafor', role: 'Couple' },
    { key: 's2', name: 'Ben Ruiz', role: 'Couple' },
    { key: 'venue', name: 'Jordan Lee', role: 'Your venue' },
  ],
}
